import React, { useCallback, useEffect, useState } from 'react';
import {
    ArrowPathIcon,
    SunIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    PencilSquareIcon,
    XCircleIcon,
    ArrowUpCircleIcon,
    ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import {
    fetchMorningRounds,
    fetchRecommendations,
    fetchFieldDrilldown,
    reviewRecommendation,
    MorningRoundsField,
    AdvisoryRecommendation,
    ReviewDecision,
} from '../services/advisoryApi';
import { handleApiError } from '../utils/api';

const levelStyles: Record<string, string> = {
    high: 'bg-red-50 border-red-200 text-red-800',
    review: 'bg-amber-50 border-amber-200 text-amber-800',
    normal: 'bg-emerald-50 border-emerald-200 text-emerald-800',
};

const levelLabel: Record<string, string> = {
    high: 'High priority',
    review: 'Needs review',
    normal: 'Normal',
};

/**
 * Agronomist "Morning Rounds".
 *
 * Shows the fields most in need of a visit, computed from real records on the
 * server. Counts at the top come from the records themselves — nothing here is
 * a hard-coded sample.
 */
const MorningRoundsPage: React.FC = () => {
    const [fields, setFields] = useState<MorningRoundsField[]>([]);
    const [buckets, setBuckets] = useState({ assessed: 0, high: 0, needReview: 0, normal: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [crop, setCrop] = useState('');
    const [region, setRegion] = useState('');
    const [reviewFor, setReviewFor] = useState<{ field: MorningRoundsField; recommendations: AdvisoryRecommendation[] } | null>(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchMorningRounds({ crop: crop || undefined, region: region || undefined });
            setFields(data.fields || []);
            setBuckets(data.buckets || { assessed: 0, high: 0, needReview: 0, normal: 0 });
            if (data.unavailable) {
                setError('Field records could not be read right now.');
            }
        } catch (err) {
            setError(handleApiError(err));
            setFields([]);
        } finally {
            setLoading(false);
        }
    }, [crop, region]);

    useEffect(() => { load(); }, [load]);

    const openReview = async (field: MorningRoundsField) => {
        try {
            const recs = await fetchRecommendations({ field_id: String(field.fieldId), status: 'PENDING_REVIEW' });
            setReviewFor({ field, recommendations: recs });
        } catch (err) {
            setError(handleApiError(err));
        }
    };

    const decide = async (recommendation: AdvisoryRecommendation, decision: ReviewDecision, notes: string, finalText: string) => {
        setBusy(true);
        try {
            await reviewRecommendation(recommendation.id, decision, {
                notes: notes || undefined,
                final_recommendation: decision === 'modify' ? finalText : undefined,
            });
            setNotice(`Recommendation #${recommendation.id} ${decision.replace('_', ' ')}d.`);
            setReviewFor((current) =>
                current
                    ? { ...current, recommendations: current.recommendations.filter((r) => r.id !== recommendation.id) }
                    : current,
            );
            await load();
        } catch (err) {
            setError(handleApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const openField = async (field: MorningRoundsField) => {
        // Drill-down proves the whole chain is reachable from a round card.
        try {
            await fetchFieldDrilldown(field.fieldId);
            window.location.assign(`/dashboard/activities?field=${field.fieldId}`);
        } catch (err) {
            setError(handleApiError(err));
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <SunIcon className="h-7 w-7 text-amber-500" />
                    <div>
                        <h1 className="text-xl font-bold text-slate-800">Morning Rounds</h1>
                        <p className="text-sm text-slate-500">Prioritised fields for today, from live field records.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        className="border rounded-md px-3 py-1.5 text-sm"
                        placeholder="Filter crop"
                        value={crop}
                        onChange={(e) => setCrop(e.target.value)}
                    />
                    <input
                        className="border rounded-md px-3 py-1.5 text-sm"
                        placeholder="Filter region"
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                    />
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-1 rounded-md bg-slate-800 text-white px-3 py-1.5 text-sm"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Fields assessed" value={buckets.assessed} />
                <Stat label="High priority" value={buckets.high} tone="text-red-600" />
                <Stat label="Need review" value={buckets.needReview} tone="text-amber-600" />
                <Stat label="Normal" value={buckets.normal} tone="text-emerald-600" />
            </div>

            {notice && <div className="rounded-md bg-emerald-50 text-emerald-800 px-4 py-2 text-sm">{notice}</div>}
            {error && (
                <div className="rounded-md bg-red-50 text-red-800 px-4 py-2 text-sm flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-4 w-4" /> {error}
                </div>
            )}

            {loading ? (
                <div className="text-slate-500 text-sm">Loading fields…</div>
            ) : fields.length === 0 ? (
                <div className="text-slate-500 text-sm">No fields to round on right now.</div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {fields.map((field) => (
                        <div key={field.fieldId} className={`border rounded-lg p-4 ${levelStyles[field.level] || ''}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-semibold text-slate-800">
                                        {field.farmerName || 'Unnamed farm'} · {field.fieldName || `Field ${field.fieldId}`}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {field.cropType || 'crop unknown'}{field.district ? ` · ${field.district}` : ''}
                                    </div>
                                </div>
                                <span className="text-xs font-semibold uppercase tracking-wide">{levelLabel[field.level]}</span>
                            </div>

                            {field.reasons.length > 0 && (
                                <ul className="mt-3 list-disc list-inside text-sm text-slate-700 space-y-0.5">
                                    {field.reasons.map((reason, i) => <li key={i}>{reason}</li>)}
                                </ul>
                            )}

                            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                                <span>Open issues: {field.openIssues}</span>
                                <span>Overdue tasks: {field.overdueAssignments}</span>
                                <span>Awaiting review: {field.pendingRecommendations}</span>
                                {field.daysSinceLastObservation !== null && (
                                    <span>Last observation: {field.daysSinceLastObservation}d ago</span>
                                )}
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    onClick={() => openReview(field)}
                                    className="inline-flex items-center gap-1 rounded-md bg-slate-800 text-white px-3 py-1.5 text-xs"
                                >
                                    <ClipboardDocumentListIcon className="h-4 w-4" /> Review advice
                                </button>
                                <button
                                    onClick={() => openField(field)}
                                    className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs"
                                >
                                    Open field
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {reviewFor && (
                <ReviewDialog
                    field={reviewFor.field}
                    recommendations={reviewFor.recommendations}
                    busy={busy}
                    onClose={() => setReviewFor(null)}
                    onDecide={decide}
                />
            )}
        </div>
    );
};

const Stat: React.FC<{ label: string; value: number; tone?: string }> = ({ label, value, tone }) => (
    <div className="rounded-lg border bg-white p-3">
        <div className={`text-2xl font-bold ${tone || 'text-slate-800'}`}>{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
    </div>
);

const ReviewDialog: React.FC<{
    field: MorningRoundsField;
    recommendations: AdvisoryRecommendation[];
    busy: boolean;
    onClose: () => void;
    onDecide: (r: AdvisoryRecommendation, d: ReviewDecision, notes: string, finalText: string) => void;
}> = ({ field, recommendations, busy, onClose, onDecide }) => {
    const [notes, setNotes] = useState('');
    const [finalText, setFinalText] = useState('');

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-800">Advice awaiting review — Field {field.fieldId}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700">Close</button>
                </div>

                {recommendations.length === 0 ? (
                    <p className="mt-4 text-sm text-slate-500">No recommendations are awaiting review for this field.</p>
                ) : (
                    recommendations.map((r) => (
                        <div key={r.id} className="mt-4 border rounded-md p-4">
                            <div className="flex items-center gap-2 text-xs">
                                <span className={`px-2 py-0.5 rounded ${r.risk_level === 'high' || r.risk_level === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {r.risk_level} risk
                                </span>
                                {r.requires_human_review && <span className="text-slate-500">human review required</span>}
                            </div>
                            <p className="mt-2 text-sm text-slate-800">{r.answer}</p>
                            <p className="mt-1 text-sm font-medium text-slate-700">Recommended: {r.recommendation}</p>
                            {r.why?.length > 0 && (
                                <ul className="mt-2 list-disc list-inside text-xs text-slate-500">
                                    {r.why.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                            )}
                            {r.uncertainty && <p className="mt-2 text-xs text-slate-500">{r.uncertainty}</p>}
                            {r.image_url && (
                                <a href={r.image_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-blue-600 underline">
                                    View photo
                                </a>
                            )}

                            <div className="mt-3 space-y-2">
                                <textarea
                                    className="w-full border rounded-md p-2 text-sm"
                                    placeholder="Review note (required to modify or escalate)"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                                <textarea
                                    className="w-full border rounded-md p-2 text-sm"
                                    placeholder="Final instruction (when modifying)"
                                    value={finalText}
                                    onChange={(e) => setFinalText(e.target.value)}
                                />
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                                <ActionButton disabled={busy} onClick={() => onDecide(r, 'approve', notes, '')} icon={<CheckCircleIcon className="h-4 w-4" />} label="Approve" />
                                <ActionButton disabled={busy} onClick={() => onDecide(r, 'modify', notes, finalText)} icon={<PencilSquareIcon className="h-4 w-4" />} label="Modify" />
                                <ActionButton disabled={busy} onClick={() => onDecide(r, 'dismiss', notes, '')} icon={<XCircleIcon className="h-4 w-4" />} label="Dismiss" />
                                <ActionButton disabled={busy} onClick={() => onDecide(r, 'escalate', notes, finalText)} icon={<ArrowUpCircleIcon className="h-4 w-4" />} label="Escalate" />
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const ActionButton: React.FC<{ disabled?: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({
    disabled, onClick, icon, label,
}) => (
    <button
        disabled={disabled}
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
    >
        {icon} {label}
    </button>
);

export default MorningRoundsPage;
